import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { useToastStore, useNotificationStore } from '../store';
import { Notification } from '../types';

const AUTO_DISMISS_MS = 6000;

interface ToastCardProps {
  notification: Notification;
  onDismiss: (id: string) => void;
  onClick: (notification: Notification) => void;
}

// Each card owns its own dismiss timer — a single timer managed by the
// parent over the whole `toasts` array would get re-armed on every
// re-render (a new toast arriving, a dismiss elsewhere), so cards either
// never auto-close or all close together. `onDismiss` must stay a stable
// zustand action reference (not an inline arrow in the parent's map) or
// this effect re-fires on every render too.
const ToastCard: React.FC<ToastCardProps> = ({ notification, onDismiss, onClick }) => {
  const { t } = useTranslation();

  useEffect(() => {
    const timer = setTimeout(() => onDismiss(notification.id), AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [notification.id, onDismiss]);

  // Backend notification titles are hardcoded English constants (no
  // backend i18n) — byType maps the known types to a localized title in
  // TR/ES sessions, falling back to the raw title for any future type
  // that hasn't been added there yet.
  const title = t(`notifications.byType.${notification.type}`, { defaultValue: notification.title });

  return (
    <div
      onClick={() => onClick(notification)}
      className="pointer-events-auto cursor-pointer rounded-lg shadow-lg border px-4 py-3 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{title}</p>
          <p className="text-xs mt-0.5 text-gray-600 dark:text-gray-400 line-clamp-2">{notification.message}</p>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDismiss(notification.id);
          }}
          className="shrink-0 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
          aria-label={t('notifications.toast.dismiss') as string}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

// Reacts to every notification type (task_assigned, task_mention,
// incident_reassigned, invitation_accepted, project_invitation), not
// just mentions — the same socket push already drives the bell dropdown,
// so scoping the toast to one type would just be an inconsistent,
// arbitrarily silent subset of it.
const NotificationToast: React.FC = () => {
  const toasts = useToastStore((s) => s.toasts);
  const dismissToast = useToastStore((s) => s.dismissToast);
  const markAsRead = useNotificationStore((s) => s.markAsRead);
  const navigate = useNavigate();

  if (toasts.length === 0) return null;

  const handleClick = (n: Notification) => {
    if (!n.is_read) markAsRead(n.id).catch(() => {});
    // entity_type today is one of: task, incident, user, project_invitation,
    // workspace_invitation — only task/incident/workspace_invitation have a
    // page to land on.
    if (n.entity_type === 'task' && n.entity_id) {
      navigate(`/tasks/${n.entity_id}`);
    } else if (n.entity_type === 'incident' && n.entity_id) {
      navigate(`/incidents/${n.entity_id}`);
    } else if (n.entity_type === 'workspace_invitation' && n.entity_id) {
      // entity_id is the invitation's id, never its token — notifications
      // deliberately never carry the raw token (see
      // AuthService.notifyOfPendingInvitations). AcceptInvite.tsx has a
      // dedicated ?invitationId= mode for exactly this entry point.
      navigate(`/accept-invite?invitationId=${n.entity_id}`);
    }
    dismissToast(n.id);
  };

  return createPortal(
    // top-36 (not top-20/header-height alone) — a live check found the
    // header-height offset landing right on top of each page's own
    // top-right action button (e.g. Tasks' "Create New Task"), which
    // the toast card's own pointer-events-auto makes genuinely
    // unclickable underneath, not just visually cluttered.
    <div className="fixed top-36 right-4 z-[60] flex flex-col gap-2 w-80 pointer-events-none">
      {toasts.map((n) => (
        <ToastCard key={n.id} notification={n} onDismiss={dismissToast} onClick={handleClick} />
      ))}
    </div>,
    document.body
  );
};

export default NotificationToast;
